import { presentationLocale } from '../../i18n/calendarLabels';
import { portalAttendanceOutcome } from '../../../LambdaFunctions/shared/portalAttendance';
import { effectivePackageStatus, packageSchoolDate } from '../../../LambdaFunctions/shared/packageEnrollmentReadModels';

export const fullName = (person = {}) => [person.firstName, person.lastName].filter(Boolean).join(' ') || person.id || '';
export function money(value, language = 'pl') {
  if (typeof value !== 'number' || !Number.isFinite(value)) return language === 'en' ? 'Data unavailable' : 'Dane niedostępne';
  return new Intl.NumberFormat(presentationLocale(language), { style: 'currency', currency: 'PLN' }).format(value);
}

export function balanceSummary(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  let label = 'Saldo rozliczone';
  if (value < 0) label = 'Do rozliczenia';
  if (value > 0) label = 'Nadpłata';
  return { label, amount: Math.abs(value) };
}

export function lessonTypeLabel(classTypeId, lessonTypes = [], language = 'pl') {
  const type = lessonTypes.find(item => item.classTypeId === classTypeId);
  if (!type || !type.name) return language === 'en' ? 'Lesson type unavailable' : 'Typ zajęć niedostępny';
  const duration = Number(type.durationMinutes);
  const alreadyNamed = new RegExp(`(^|\\D)${duration}\\s*min(?:\\b|ut)`, 'i').test(type.name);
  return `${type.name}${duration > 0 && !alreadyNamed ? ` · ${duration} min` : ''}`;
}

export function polishDate(date, weekday = false, language = 'pl') {
  if (!date) return language === 'en' ? 'Date unavailable' : 'Data niedostępna';
  const parsed = new Date(`${String(date).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return language === 'en' ? 'Date unavailable' : 'Data niedostępna';
  return parsed.toLocaleDateString(language === 'en' ? 'en-GB' : 'pl-PL', { day: 'numeric', month: 'long', year: 'numeric', ...(weekday ? { weekday: 'long' } : {}), timeZone: 'Europe/Warsaw' });
}

const lessonOrder = (left, right) => `${left.date || ''} ${left.startTime || ''}`.localeCompare(`${right.date || ''} ${right.startTime || ''}`);
export function selectPortal({ snapshot, selectedStudentId = null, now = Date.now() }) {
  const byStudent = list => (list || []).filter(item => !selectedStudentId || item.studentId === selectedStudentId);
  const unknownAssociations = {};
  const unassociated = {};
  const byRelationship = (list, field, key) => {
    unassociated[key] = (list || []).filter(item => !Array.isArray(item[field]) || !item[field].length);
    unknownAssociations[key] = Boolean(selectedStudentId && unassociated[key].length);
    return (list || []).filter(item => !selectedStudentId || (Array.isArray(item[field]) && item[field].includes(selectedStudentId)));
  };
  const schedule = byStudent(snapshot.schedule).filter(item => !item.startsAt || new Date(item.startsAt).getTime() > now).sort(lessonOrder);
  const nearest = schedule.find(item => new Date(item.startsAt).getTime() > now && item.lessonStatus === 'scheduled' && item.participationStatus === 'scheduled') || null;
  const today = packageSchoolDate(now);
  const visiblePackages = (snapshot.packages || []).map(item => {
    const status = effectivePackageStatus(item, today);
    return status === item.status ? item : { ...item, status, warnings: (item.warnings || []).filter(warning => warning !== 'endingSoon') };
  });
  const packages = byRelationship(visiblePackages, 'eligibleStudentIds', 'packages');
  const attendanceLessons = byStudent(snapshot.attendance && snapshot.attendance.lessons);
  const outcomes = attendanceLessons.map(portalAttendanceOutcome);
  const present = outcomes.filter(outcome => outcome === 'present').length;
  const total = outcomes.filter(outcome => outcome === 'present' || outcome === 'absent').length;
  const history = snapshot.history || {};
  return {
    ledger: snapshot.ledger,
    schedule,
    nearest,
    schedulePreview: schedule.length === 1 && nearest === schedule[0] ? [] : schedule.slice(0, 2),
    packages,
    packagePreview: [...packages].sort((a, b) => {
      const rank = (item) => {
        if (item.status === 'awaitingPayment' || (item.warnings || []).length) return 0;
        return item.status === 'active' ? 1 : 2;
      };
      return rank(a) - rank(b);
    }).slice(0, 2),
    summaries: byStudent(snapshot.lessonSummaries).sort((a, b) => lessonOrder(b, a)),
    materials: byRelationship(snapshot.materials, 'studentIds', 'materials'),
    historyLessons: byStudent(history.lessons).sort((a, b) => lessonOrder(b, a)),
    courses: byRelationship(history.courses, 'studentIds', 'courses'),
    invoices: history.invoices || [],
    attendanceLessons,
    attendance: total ? { present, total, rate: Math.round((present / total) * 100) } : null,
    unknownAssociations,
    unassociated,
  };
}


export function lessonTimeLabel(lesson, lessonTypes = [], language = 'pl') {
  const type = lessonTypes.find(item => item.classTypeId === lesson.classTypeId);
  const startsAt = new Date(lesson.startsAt).getTime();
  if (!lesson.startTime) return language === 'en' ? 'Time unavailable' : 'Godzina niedostępna';
  if (!lesson.startsAt || !Number.isFinite(startsAt) || !type || !Number.isFinite(type.durationMinutes) || type.durationMinutes <= 0) return lesson.startTime;
  const end = new Date(startsAt + (type.durationMinutes * 60000));
  return `${lesson.startTime}–${end.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Warsaw' })}`;
}
